import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, ArrowRight } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ServiceTestimonials from '../../components/ServiceTestimonials';
import ContactFormDialog from '../../components/ContactFormDialog';
import { Button } from '../../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';

const TrademarkRegistration = () => {
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const pricingPlans = [
    { name: "Basic", price: "$99", features: ["Trademark Search", "Filing Assistance", "USPTO Application", "Email Support", "Standard Processing"] },
    { name: "Standard", price: "$249", popular: true, features: ["Everything in Basic", "Comprehensive Search", "Attorney Review", "Priority Support", "Office Action Response", "Faster Processing"] },
    { name: "Premium", price: "$499", features: ["Everything in Standard", "Dedicated Attorney", "24/7 Premium Support", "Multi-class Filing", "International Trademark", "Lifetime Monitoring"] }
  ];

  const process = [
    { step: "01", title: "Free Consultation", description: "Share your brand details with our trademark attorneys and receive a personalized filing strategy." },
    { step: "02", title: "Search & Filing", description: "We run a thorough USPTO search, prepare your application, and submit it following all federal requirements." },
    { step: "03", title: "Approval & Protection", description: "We monitor your application, handle office actions, and guide you through to final trademark approval." }
  ];

  const testimonials = [
    { name: "Sarah Johnson", role: "E-commerce Business Owner", text: "The trademark registration process was smooth and professional. My brand is now fully protected." },
    { name: "Michael Chen", role: "Tech Startup Founder", text: "Fast, efficient, and affordable. Got my trademark registered without any complications." },
    { name: "Emily Rodriguez", role: "Fashion Designer", text: "Excellent customer service and expert legal guidance throughout the entire registration process." },
    { name: "David Thompson", role: "Restaurant Owner", text: "Protected my brand identity with ease. The team was incredibly helpful and responsive." },
    { name: "Lisa Wang", role: "App Developer", text: "Professional trademark attorneys who truly understand business needs. Highly recommended." }
  ];

  const faqs = [
    { question: "How long does trademark registration take?", answer: "Trademark registration typically takes 8-12 months from filing to approval, depending on USPTO processing times and any office actions that may arise." },
    { question: "What can be trademarked?", answer: "Business names, logos, slogans, product names, service marks, and any distinctive brand identifier that distinguishes your goods or services." },
    { question: "Do I need an attorney to register a trademark?", answer: "While not legally required, having a trademark attorney significantly increases your approval rate and helps avoid costly filing mistakes." },
    { question: "What's the difference between TM and the registered symbol?", answer: "TM indicates an unregistered trademark claim, while the registered symbol can only be used after your trademark is officially registered with the USPTO." },
    { question: "How long does trademark protection last?", answer: "A registered trademark lasts 10 years and can be renewed indefinitely, as long as you continue using the mark in commerce and file required maintenance documents." }
  ];

  return (
    <div className="min-h-screen bg-wine-950">
      <Header onOpenContactForm={() => setIsContactFormOpen(true)} />
      
      <section className="pt-24 pb-16 bg-gradient-to-b from-wine-900/50 to-wine-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/" className="inline-flex items-center text-gold-400 hover:text-gold-500 mb-6 text-sm font-medium transition-colors">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Home
          </Link>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-4">US Trademark Registration</h1>
          <p className="text-lg text-wine-300 max-w-2xl mx-auto">Protect your brand with professional federal trademark registration. Expert USPTO filing starting at $99.</p>
        </div>
      </section>

      <section className="py-20 bg-cream-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="rounded-2xl overflow-hidden h-80 shadow-lg">
              <img src="https://images.unsplash.com/photo-1774891937497-96df6a794bd9?w=800&h=600&fit=crop" alt="Official trademark seal and legal stamp" className="w-full h-full object-cover" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-wine-500 mb-3">About This Service</p>
              <h2 className="text-3xl font-bold text-wine-950 mb-4">What Is Trademark Registration?</h2>
              <p className="text-stone-600 leading-relaxed mb-4">A trademark is a unique identifier that distinguishes your business from competitors. Registering with the USPTO provides federal protection and exclusive rights to use your mark nationwide.</p>
              <p className="text-stone-600 leading-relaxed mb-6">Our trademark attorneys manage the full process: comprehensive search, application preparation, USPTO filing, and office action response. Your brand gets the legal protection it deserves.</p>
              <Button onClick={() => setIsContactFormOpen(true)} className="bg-wine-600 hover:bg-wine-700 text-white transition-colors">Get Started Today</Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-wine-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-400 mb-3 text-center">How It Works</p>
          <h2 className="text-3xl font-bold text-white text-center mb-12">Our 3-Step Process</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {process.map((item) => (
              <div key={item.step} className="bg-wine-950 p-8 rounded-xl border border-wine-800/40 hover:border-gold-500/30 hover:-translate-y-1 hover:shadow-xl transition-all duration-300">
                <div className="w-10 h-10 bg-wine-600 rounded-lg flex items-center justify-center text-white font-bold text-sm mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                <p className="text-sm text-wine-400 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-wine-500 mb-3 text-center">Pricing</p>
          <h2 className="text-3xl font-bold text-wine-950 text-center mb-4">Transparent Filing Packages</h2>
          <p className="text-center text-stone-500 mb-12">Choose the plan that fits your trademark needs</p>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {pricingPlans.map((plan) => (
              <div key={plan.name} className={`relative bg-white p-8 rounded-xl border-2 ${plan.popular ? 'border-wine-600 shadow-xl shadow-wine-600/10' : 'border-stone-200'} hover:-translate-y-1 hover:shadow-xl hover:border-gold-400 transition-all duration-300`}>
                {plan.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2"><span className="bg-wine-600 text-white px-3 py-1 rounded-full text-xs font-bold">MOST POPULAR</span></div>}
                <h3 className="text-xl font-bold text-wine-950 mb-2">{plan.name}</h3>
                <div className="text-4xl font-bold text-wine-950 mb-6">{plan.price}</div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((f, i) => (<li key={i} className="flex items-start"><Check className="w-4 h-4 text-wine-600 mr-2 mt-0.5 flex-shrink-0" /><span className="text-sm text-stone-600">{f}</span></li>))}
                </ul>
                <Button onClick={() => setIsContactFormOpen(true)} className={`w-full ${plan.popular ? 'bg-wine-600 hover:bg-wine-700 text-white' : 'bg-stone-100 hover:bg-gold-500 hover:text-wine-950 text-stone-800'} transition-colors`}>Choose {plan.name}</Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-wine-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">Why Choose Our Trademark Services?</h2>
              <div className="space-y-5">
                {['Expert attorneys with deep USPTO experience', 'Transparent pricing with no hidden charges', 'Fast processing and priority filing options', 'Comprehensive support from search to approval'].map((t, i) => (
                  <div key={i} className="flex items-start gap-3"><Check className="w-5 h-5 text-gold-400 mt-0.5 flex-shrink-0" /><span className="text-wine-200">{t}</span></div>
                ))}
              </div>
            </div>
            <div className="bg-wine-800 rounded-2xl p-10 text-center border border-wine-700/40">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-400 mb-3">Ready to start?</p>
              <h3 className="text-2xl font-bold text-white mb-4">Protect Your Brand Today</h3>
              <p className="text-wine-300 text-sm mb-6">Join thousands of businesses that trust Trademark Text Logo for their trademark registration needs.</p>
              <Button onClick={() => setIsContactFormOpen(true)} className="bg-gold-500 hover:bg-gold-400 text-wine-950 font-semibold px-8 transition-colors">
                Start Registration <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <ServiceTestimonials testimonials={testimonials} serviceName="Trademark Registration" />

      <section className="py-20" style={{ background: 'linear-gradient(180deg, #1A0A0E 0%, #0D0507 100%)' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-400 mb-3 text-center">FAQ</p>
          <h2 className="text-3xl font-bold text-white text-center mb-10">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`faq-${index}`} className="bg-wine-900/50 rounded-xl px-6 border border-wine-800/40 hover:border-gold-400/30 transition-colors data-[state=open]:border-gold-400/40">
                <AccordionTrigger className="text-left text-base font-medium text-wine-100 hover:text-gold-400 py-5 [&[data-state=open]]:text-gold-400">{faq.question}</AccordionTrigger>
                <AccordionContent className="text-wine-300 text-sm leading-relaxed pb-5">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <Footer onOpenContactForm={() => setIsContactFormOpen(true)} />
      <ContactFormDialog isOpen={isContactFormOpen} onClose={() => setIsContactFormOpen(false)} />
    </div>
  );
};

export default TrademarkRegistration;
